import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { ArrowLeft, LogOut, Save, Package, Clock, Factory, Truck, PackageCheck, Search, X, Settings, CreditCard, Copy, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import OrderBreakdown from "@/components/OrderBreakdown";
import { AUTH } from "@/constants/testIds/app";
import { ADMIN } from "@/constants/testIds/cardRequests";
import { ADMIN_FILTERS, PRICING } from "@/constants/testIds/pricing";

const STATUS_META = {
  pending: { label: "Richiesta ricevuta", color: "bg-[#E9A94E]/12 text-[#B57F1F]", dot: "bg-[#E9A94E]" },
  approved: { label: "Approvata", color: "bg-[#4A8A6E]/12 text-[#3E7A5E]", dot: "bg-[#4A8A6E]" },
  ordered: { label: "Ordinata", color: "bg-[#4A8A6E]/12 text-[#3E7A5E]", dot: "bg-[#4A8A6E]" },
  production: { label: "In produzione", color: "bg-[#5B7CFB]/12 text-[#4560D8]", dot: "bg-[#5B7CFB]" },
  shipped: { label: "Spedita", color: "bg-[#00F2F4]/12 text-[#00D6D8]", dot: "bg-[#00F2F4]" },
  delivered: { label: "Consegnata", color: "bg-[#0F5132]/12 text-[#0F5132]", dot: "bg-[#0F5132]" },
  cancelled: { label: "Annullata", color: "bg-[#C6423B]/12 text-[#C6423B]", dot: "bg-[#C6423B]" },
};

const STATUS_ORDER = ["pending", "approved", "ordered", "production", "shipped", "delivered", "cancelled"];

function SummaryTile({ label, value, sub, icon: Icon, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className="p-5 bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
          {label}
        </div>
        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${accent || "bg-[var(--bg-3)] text-[var(--fg-1)]"}`}>
          <Icon size={14} strokeWidth={1.7} />
        </div>
      </div>
      <div className="mt-3 font-serif text-3xl text-[var(--fg-1)] leading-none">{value}</div>
      {sub && <div className="mt-1.5 text-xs text-[var(--fg-3)]">{sub}</div>}
    </div>
  );
}

export default function AdminCardRequests() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("pending");
  const [internalNotes, setInternalNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [rs, ss] = await Promise.all([
        api.get("/admin/card-requests"),
        api.get("/admin/card-requests/summary"),
      ]);
      setRequests(rs.data);
      setSummary(ss.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore caricamento");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openDetails = (req) => {
    setSelected(req);
    setStatus(req.status);
    setInternalNotes(req.internal_notes || "");
  };

  const closeDetails = () => setSelected(null);

  const saveDetails = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/card-requests/${selected.request_id}`, {
        status,
        internal_notes: internalNotes,
      });
      setRequests((prev) => prev.map((r) => (r.request_id === data.request_id ? data : r)));
      setSelected(data);
      toast.success("Richiesta aggiornata");
      // Refresh summary
      const ss = await api.get("/admin/card-requests/summary");
      setSummary(ss.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const generatePaymentLink = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/card-requests/${selected.request_id}/payment-link`, {
        origin_url: window.location.origin,
      });
      const refreshed = await api.get(`/admin/card-requests/${selected.request_id}`);
      setSelected(refreshed.data);
      setRequests((prev) => prev.map((r) => (r.request_id === refreshed.data.request_id ? refreshed.data : r)));
      toast.success("Link di pagamento generato e inviato al cliente");
      navigator.clipboard.writeText(data.payment_url).catch(() => {});
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore nella generazione del link");
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = summary?.pending ?? "—";
  const toOrder = summary?.to_order_qty ?? "—";

  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [r.business_name, r.contact_name, r.email, r.request_id].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [requests, searchQuery, statusFilter]);

  const hasActiveFilters = searchQuery.trim().length > 0 || statusFilter !== "all";

  return (
    <div className="min-h-screen bg-[var(--bg-1)]" data-testid={ADMIN.container}>
      <Toaster position="top-right" theme={theme} />

      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="font-serif text-3xl text-[var(--fg-1)] tracking-tight leading-none">
              Recen<span className="italic text-[#00F2F4]">Up</span>
            </div>
            <Link
              to="/dashboard"
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]"
              style={{ transition: "color 0.2s ease" }}
            >
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <Link
              data-testid={PRICING.navLink}
              to="/admin/settings/card-pricing"
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]"
              style={{ transition: "color 0.2s ease" }}
            >
              <Settings size={14} /> Prezzi Card
            </Link>
            <div className="hidden md:flex items-center gap-3 text-xs text-[var(--fg-3)] uppercase tracking-[0.2em]">
              <div className="h-4 w-px bg-[var(--border-c)]" />
              Area amministratore
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="text-xs text-[var(--fg-3)] font-sans hidden md:block">
              {user?.email}
            </div>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="h-9 w-9 rounded-full hover:bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-2)]"
              style={{ transition: "background-color 0.2s ease" }}
              title="Esci"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[#00F2F4] font-semibold">
            Amministrazione
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-[var(--fg-1)] tracking-tight">
            Richieste <span className="italic">card</span>
          </h1>
          <p className="mt-2 text-sm text-[var(--fg-3)]">
            Tutte le richieste ricevute dai clienti. Cliccando su una riga puoi aggiornare stato e annotazioni interne.
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <SummaryTile label="In attesa" value={pendingCount} icon={Clock} testId={ADMIN.summary.pending} accent="bg-[#E9A94E]/15 text-[#B57F1F]" />
          <SummaryTile label="Card da ordinare" value={toOrder} sub="approvate + ordinate + produzione" icon={Package} testId={ADMIN.summary.toOrder} accent="bg-[#00F2F4]/12 text-[#00D6D8]" />
          <SummaryTile label="In produzione" value={summary?.in_production ?? "—"} icon={Factory} testId={ADMIN.summary.production} accent="bg-[#5B7CFB]/12 text-[#4560D8]" />
          <SummaryTile label="Spedite" value={summary?.shipped ?? "—"} icon={Truck} testId={ADMIN.summary.shipped} accent="bg-[#00F2F4]/12 text-[#00D6D8]" />
          <SummaryTile label="Consegnate" value={summary?.delivered ?? "—"} icon={PackageCheck} testId={ADMIN.summary.delivered} accent="bg-[#0F5132]/12 text-[#0F5132]" />
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
            <Input
              data-testid={ADMIN_FILTERS.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per attività, referente, email o ID richiesta…"
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid={ADMIN_FILTERS.statusSelect}>
              <SelectValue placeholder="Tutti gli stati" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <button
              data-testid={ADMIN_FILTERS.clearBtn}
              onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-4 text-xs font-sans"
              style={{ transition: "border-color 0.2s ease" }}
            >
              <X size={12} /> Azzera filtri
            </button>
          )}
        </div>

        {/* Table */}
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl overflow-hidden shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
        >
          <div className="hidden md:grid grid-cols-[110px_1fr_90px_120px_150px_100px] gap-4 px-6 py-3 border-b border-[var(--border-c)] bg-[var(--bg-3)]/40 text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] font-sans font-semibold">
            <div>ID</div>
            <div>Attività</div>
            <div>Qtà</div>
            <div>Data</div>
            <div>Stato</div>
            <div className="text-right">Azioni</div>
          </div>
          <div className="divide-y divide-[var(--border-c)]">
            {filteredRequests.length === 0 ? (
              <div className="p-10 text-center text-sm text-[var(--fg-3)]">
                {requests.length === 0
                  ? "Nessuna richiesta ancora ricevuta."
                  : "Nessuna richiesta corrisponde ai filtri selezionati."}
              </div>
            ) : (
              filteredRequests.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                return (
                  <div
                    key={r.request_id}
                    data-testid={ADMIN.tableRow(r.request_id)}
                    className="md:grid md:grid-cols-[110px_1fr_90px_120px_150px_100px] flex flex-col md:items-center gap-3 md:gap-4 px-6 py-4 hover:bg-[var(--bg-3)]/40"
                    style={{ transition: "background-color 0.15s ease" }}
                  >
                    <div className="font-mono text-xs text-[var(--fg-1)] font-semibold">#{r.request_id}</div>
                    <div className="text-sm text-[var(--fg-1)]">
                      <div className="font-semibold">{r.business_name || "—"}</div>
                      <div className="text-xs text-[var(--fg-3)]">{r.contact_name} · {r.email}</div>
                    </div>
                    <div className="text-sm text-[var(--fg-1)]">
                      {r.quantity}
                      {r.is_custom && <span className="ml-1 text-[10px] uppercase tracking-[0.15em] text-[#00F2F4]">custom</span>}
                    </div>
                    <div className="text-xs text-[var(--fg-3)]">
                      {r.created_at ? format(new Date(r.created_at), "d MMM yyyy", { locale: itLocale }) : "—"}
                    </div>
                    <div>
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${meta.color}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                        {meta.label}
                      </span>
                    </div>
                    <div className="md:text-right">
                      <button
                        data-testid={ADMIN.openDetailsBtn(r.request_id)}
                        onClick={() => openDetails(r)}
                        className="inline-flex items-center gap-1 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-3 py-1.5 text-xs font-sans"
                        style={{ transition: "border-color 0.2s ease" }}
                      >
                        Dettagli
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </main>

      {/* Details modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDetails()}>
        <DialogContent className="bg-[var(--bg-1)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={ADMIN.detailsModal}>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[var(--fg-1)]">
                  Richiesta <span className="font-mono text-lg text-[var(--fg-3)]">#{selected.request_id}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Attività</div>
                  <div className="mt-1 text-[var(--fg-1)] font-semibold">{selected.business_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Quantità</div>
                  <div className="mt-1 text-[var(--fg-1)]">
                    {selected.quantity} card
                    {selected.is_custom && <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#00F2F4]">personalizzata</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Referente</div>
                  <div className="mt-1 text-[var(--fg-1)]">{selected.contact_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Email</div>
                  <div className="mt-1 text-[var(--fg-1)] break-all">{selected.email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Telefono</div>
                  <div className="mt-1 text-[var(--fg-1)]">{selected.phone}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Richiesta il</div>
                  <div className="mt-1 text-[var(--fg-1)]">{format(new Date(selected.created_at), "d MMM yyyy, HH:mm", { locale: itLocale })}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Indirizzo di spedizione</div>
                  <div className="mt-1 text-[var(--fg-1)] whitespace-pre-line">{selected.shipping_address}</div>
                </div>
                {selected.notes && (
                  <div className="md:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">Note del cliente</div>
                    <div className="mt-1 text-[var(--fg-2)] whitespace-pre-line italic">"{selected.notes}"</div>
                  </div>
                )}
              </div>

              {selected.pricing && (
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] mb-2">
                    Riepilogo prezzo
                  </div>
                  <OrderBreakdown
                    pricing={selected.pricing}
                    quantity={selected.quantity}
                    showLockStatus
                  />
                  {!selected.pricing.locked && (
                    <p className="mt-2 text-[11px] text-[var(--fg-3)]">
                      Il prezzo verrà bloccato quando la richiesta passa allo stato <em>Approvata</em>.
                    </p>
                  )}
                </div>
              )}

              {/* Payment section (only after price is locked) */}
              {selected.pricing?.locked && (
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] mb-2">
                    Pagamento
                  </div>
                  {selected.payment_status === "paid" ? (
                    <div
                      data-testid="admin-payment-paid"
                      className="p-4 rounded-xl bg-[#4A8A6E]/8 border border-[#4A8A6E]/25 flex items-center gap-3"
                    >
                      <div className="h-9 w-9 rounded-full bg-[#4A8A6E]/15 text-[#3E7A5E] flex items-center justify-center">
                        <CheckCircle2 size={16} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-serif text-[var(--fg-1)]">Pagamento ricevuto</div>
                        <div className="text-xs text-[var(--fg-3)] mt-0.5">
                          {selected.paid_at ? `Confermato il ${format(new Date(selected.paid_at), "d MMM yyyy, HH:mm", { locale: itLocale })}` : "Confermato"}
                        </div>
                      </div>
                    </div>
                  ) : selected.payment_url ? (
                    <div className="p-4 rounded-xl bg-[var(--bg-3)]/50 border border-[var(--border-c)] space-y-3">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)]">
                        <Clock size={11} /> In attesa di pagamento
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          value={selected.payment_url}
                          readOnly
                          onClick={(e) => e.target.select()}
                          className="font-mono text-xs"
                          data-testid="admin-payment-link-input"
                        />
                        <button
                          data-testid="admin-copy-payment-link"
                          onClick={() => {
                            navigator.clipboard.writeText(selected.payment_url);
                            toast.success("Link copiato negli appunti");
                          }}
                          className="h-9 w-9 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] flex items-center justify-center text-[var(--fg-2)]"
                          style={{ transition: "border-color 0.2s ease" }}
                          title="Copia link"
                        >
                          <Copy size={14} />
                        </button>
                        <a
                          href={selected.payment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-9 w-9 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] flex items-center justify-center text-[var(--fg-2)]"
                          style={{ transition: "border-color 0.2s ease" }}
                          title="Apri link"
                        >
                          <ExternalLink size={14} />
                        </a>
                      </div>
                      <div className="text-[11px] text-[var(--fg-3)]">
                        L'email col link è stata inviata a {selected.email}. Il cliente può pagare con Carta, Apple Pay o Google Pay.
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid="admin-generate-payment-link"
                      onClick={generatePaymentLink}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-3 text-sm font-sans font-semibold disabled:opacity-60"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      <CreditCard size={15} /> {saving ? "Generazione…" : "Genera link di pagamento"}
                    </button>
                  )}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-[var(--border-c)] space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] mb-2">Stato attuale</div>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid={ADMIN.statusSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_ORDER.map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--fg-3)] mb-2">
                    Note interne <span className="normal-case text-[#00D6D8] font-sans">(visibili solo agli admin)</span>
                  </div>
                  <Textarea
                    data-testid={ADMIN.internalNotesInput}
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    placeholder="Es. fornitore contattato, spedizione prevista..."
                    className="min-h-[80px]"
                  />
                </div>
              </div>

              <DialogFooter className="gap-2 mt-6">
                <button
                  onClick={closeDetails}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-2.5 text-sm font-sans"
                  style={{ transition: "border-color 0.2s ease" }}
                >
                  Chiudi
                </button>
                <Button
                  data-testid={ADMIN.saveDetailsBtn}
                  onClick={saveDetails}
                  disabled={saving}
                  className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] gap-2"
                >
                  <Save size={14} /> Salva modifiche
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
